import * as React from 'react';
import { BaSeI18nConsumer } from '../../../contexts/i18n';
import { BaSeTheme } from '../../../theme';
import { getFormattedDataset } from '../../../utils/dataset-utils';
import { BaSeIcon } from '../../image/icon';
import { BaSeLoadingSpinner } from '../../loading/spinner';
import { ButtonAnchorProps } from '../button-link';
import { ButtonProps } from '../button-props';
import type { IconButtonSize } from './icon-button-shared';
import {
  getIconButtonBorderRadius,
  getIconButtonBoxShadowFocus,
  getIconButtonIconSize,
  getIconButtonMessageComponent,
  getIconButtonPadding,
  getIconButtonStyle,
  getIconButtonWidth,
} from './icon-button-shared';
import * as Styled from './icon-button-styled';

export interface IconButtonProps
  extends Omit<
    ButtonProps,
    | 'textColor'
    | 'direction'
    | 'isBig'
    | 'isBold'
    | 'leftIcon'
    | 'descriptionLeftIcon'
    | 'rightIcon'
    | 'descriptionRightIcon'
    | 'spacedIcons'
    | 'width'
    | 'loadingText'
    | 'size'
    | 'tooltip'
    | 'popover'
  > {
  size?: IconButtonSize;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  children?: React.ReactNode;
}

interface IconButtonBaseButtonProps extends IconButtonProps {
  element?: 'button';
  buttonRef?: React.Ref<HTMLButtonElement>;
}

interface IconButtonBaseAnchorProps
  extends Omit<
    IconButtonProps,
    'buttonType' | 'onClick' | 'onFocus' | 'onBlur'
  > {
  element: 'anchor';
  anchorProps: ButtonAnchorProps;
  buttonRef: React.Ref<HTMLAnchorElement>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

export type IconButtonBaseProps =
  | IconButtonBaseButtonProps
  | IconButtonBaseAnchorProps;

export const IconButtonBase: React.FC<IconButtonBaseProps> = (props) => {
  const {
    id,
    name,
    dataset,
    nameIcon,
    descriptionIcon,
    value,
    type = 'primary',
    color = BaSeTheme.components.button.colors.default,
    shape = 'circle',
    sizeIcon = 'small',
    size = 'medium',
    isDisabled = false,
    sideButton = 'none',
    autoFocus = false,
    isLoading = false,
    children,
  } = props;

  if (!nameIcon && !value) {
    throw Error(
      '[BaSeIconButton] error: Nome do ícone ou um valor textual é obrigatório!',
    );
  }

  const borderRadius = getIconButtonBorderRadius(shape);

  const iconSize = React.useMemo(
    () => getIconButtonIconSize(size, sizeIcon),
    [size, sizeIcon],
  );

  const padding = React.useMemo(
    () => getIconButtonPadding(size, iconSize),
    [size, iconSize],
  );

  const width = React.useMemo(() => getIconButtonWidth(size), [size]);

  const typeMapped = getIconButtonStyle({ color, type, isDisabled });

  const {
    background,
    border,
    backgroundOnFocus,
    iconColor,
    hoverColor,
    boxShadowFocus,
    borderHoverColor,
    iconHoverColor,
  } = typeMapped;

  const MessageComponent = React.useMemo(
    () => getIconButtonMessageComponent(size),
    [size],
  );

  const [isHover, setIsHover] = React.useState<boolean>(false);

  const formattedDataset = getFormattedDataset(dataset);
  const visibleIconColor =
    isHover && !!iconHoverColor ? iconHoverColor : iconColor;

  const content = (
    <>
      <BaSeI18nConsumer>
        {({ getMessage }) =>
          isLoading ? (
            <BaSeLoadingSpinner
              diameter={iconSize}
              color={visibleIconColor}
              description={getMessage('loading.description')}
              disabled={isDisabled}
              type="tertiary"
            />
          ) : nameIcon ? (
            <BaSeIcon
              description={
                descriptionIcon?.trim() ||
                getMessage('buttonIcon.defaultDescription')
              }
              name={nameIcon}
              size={iconSize}
              color={visibleIconColor}
              colorTransition="fast"
            />
          ) : (
            <div
              style={{
                width: iconSize,
                height: iconSize,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <MessageComponent color={visibleIconColor}>
                {value}
              </MessageComponent>
            </div>
          )
        }
      </BaSeI18nConsumer>
      {children}
    </>
  );

  const commonStyledProps = {
    id,
    name,
    ...formattedDataset,
    width,
    autoFocus,
    isSecondary: type === 'secondary',
    backgroundOnFocus,
    border: isHover && !!borderHoverColor ? borderHoverColor : border,
    borderRadius,
    backgroundColor: background,
    backgroundOnHover: hoverColor,
    boxShadowOnFocus: getIconButtonBoxShadowFocus(size, boxShadowFocus),
    padding: border === 'none' ? padding : padding - 1,
    sideButton,
  };

  if (props.element === 'anchor') {
    const {
      anchorProps,
      buttonRef,
      onClick,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
    } = props;

    return (
      <Styled.IconAnchorButton
        {...anchorProps}
        {...commonStyledProps}
        ref={buttonRef}
        onMouseEnter={(event) => {
          setIsHover(true);
          onMouseEnter?.(
            event as unknown as React.MouseEvent<HTMLButtonElement>,
          );
          anchorProps.onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          setIsHover(false);
          onMouseLeave?.(
            event as unknown as React.MouseEvent<HTMLButtonElement>,
          );
          anchorProps.onMouseLeave?.(event);
        }}
        onClick={(event) => {
          onClick?.(event);
          anchorProps.onClick?.(event);
        }}
        onFocus={(event) => {
          onFocus?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
          anchorProps.onFocus?.(event);
        }}
        onBlur={(event) => {
          onBlur?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
          anchorProps.onBlur?.(event);
        }}
      >
        {content}
      </Styled.IconAnchorButton>
    );
  }

  const {
    buttonRef,
    buttonType = 'button',
    onClick,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
  } = props;

  return (
    <Styled.IconButton
      {...commonStyledProps}
      ref={buttonRef}
      disabled={isDisabled}
      type={buttonType}
      onMouseEnter={(event) => {
        setIsHover(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setIsHover(false);
        onMouseLeave?.(event);
      }}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {content}
    </Styled.IconButton>
  );
};

export const BaSeIconButton = React.forwardRef<
  HTMLButtonElement,
  IconButtonProps
>((props, ref) => <IconButtonBase {...props} buttonRef={ref} />);

BaSeIconButton.displayName = 'BaSeIconButton';
